import {
  FeatureName,
  useCurrentAccount,
  useCurrentWorkspace,
  useFeatures,
  useIsFeatureEnabled,
  WorkspaceType,
} from '@gripp/shared-logic';
// Crop Circle tokens (`colors`), not the mutable `Colors`: this route can
// render before the theme is applied, when `Colors.primary` still holds the
// legacy orange and would mismatch the native launch screen.
import { Colors, colors, LoginHeader } from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, PaperProvider } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';

// Render nothing for this long so fast loads never flash the splash — the
// page stays browser-white until the landing decision resolves. Only loads
// slower than this show the splash below.
const SPLASH_APPEARANCE_DELAY_MS = 500;

export const Home = () => {
  const navigate = useNavigate();

  // Landing decision (OEM Home Dashboard, PR 1b): flag-on OEM lands on /home;
  // every other workspace type and every flag-off case lands on /activities.
  // To avoid a flash/bounce, keep showing the splash below until the account
  // and features have settled, then decide exactly once.
  // storageOnly=true so the landing decision, the page self-guard, and the nav
  // all resolve the workspace identically. A global admin who hasn't
  // selected/impersonated a workspace resolves to null here (not their home
  // account.workspace) and lands on /activities — matching the nav, which shows
  // no Home item for them. Without this they'd land on /home and the page
  // self-guard would bounce them straight back.
  const workspace = useCurrentWorkspace(true);
  const { account, isFetching: accountFetching } = useCurrentAccount();
  const { data: features, isLoading: featuresLoading } = useFeatures();
  const isOemHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.OemHomeDashboard
  );
  const isPulseHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.PulseHomeDashboard
  );

  const [splashVisible, setSplashVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(
      () => setSplashVisible(true),
      SPLASH_APPEARANCE_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, []);

  // Wait only while the account or features are GENUINELY still loading — not
  // on a workspace that is legitimately absent. useCurrentWorkspace() returns
  // null/undefined both while fetching AND for accounts with no resolved
  // workspace (a global admin who hasn't selected/impersonated one, lonely
  // larry). Gating on `workspace` being truthy stranded those users on this
  // splash forever; instead settle on the account, then treat a falsy
  // workspace as "not OEM" and fall through to /activities.
  const accountResolving = !account && accountFetching;
  const featuresResolving = featuresLoading || !features;
  const isResolving = accountResolving || featuresResolving;

  useEffect(() => {
    if (isResolving) return;

    const isOem = workspace?.type === WorkspaceType.Oem;
    const isProducer = workspace?.type === WorkspaceType.Producer;
    // Land on /home only when the workspace type matches a dashboard whose flag
    // is enabled (OEM→OemHomeDashboard, Producer→Pulse). Every other type and
    // every flag-off case lands on /activities.
    const landOnHome =
      (isOem && isOemHomeDashboardEnabled) ||
      (isProducer && isPulseHomeDashboardEnabled);
    navigate(landOnHome ? '/home' : '/activities', {
      replace: true,
    });
  }, [
    isResolving,
    workspace,
    isOemHomeDashboardEnabled,
    isPulseHomeDashboardEnabled,
    navigate,
  ]);

  if (!splashVisible) {
    return null;
  }

  // White theme matching the native launch screen and SplashMessageScreen,
  // so the splash blends into the white app content instead of flashing.
  return (
    <PaperProvider>
      <View style={styles.container}>
        <View style={styles.headerImage}>
          <LoginHeader fillColor={colors.brand.core} />
        </View>
        <ActivityIndicator
          size="large"
          animating={true}
          color={colors.brand.core}
        />
      </View>
    </PaperProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.role.canvas,
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerImage: {
    paddingBottom: 65,
  },
});
